import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useFilters from 'utils/hooks/useFilters';
import CustomTable from 'components/CustomTable/CustomTable';
import i18nIsoCountries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/configureStore';
import {
  notification,
  DatePicker,
  Input,
  Button,
  Modal,
  theme,
  Empty,
  Table,
} from 'antd';
import {
  PlusOutlined,
  FolderOpenOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { tppOp } from 'store/ducks/DataEntry/ThirdPartyProviders';
import { ITPP } from 'store/ducks/DataEntry/ThirdPartyProviders/types';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import { EditableColumnType } from 'components/CustomTable/CustomTable';
import useCountries from 'utils/hooks/fetchHooks/useCountries';
import {
  setProviderNames,
  set_isAddRowTpp,
  set_isTppAdded,
  set_tpps,
  set_addRowCountTpp,
  set_certificates,
  set_tppId,
  set_nameOfTpp,
  set_isTppUpdated,
} from 'store/ducks/DataEntry/ThirdPartyProviders/tppSlice';
import useTppStatus from 'utils/hooks/useTppStatus';
import { entityOp } from 'store/ducks/DataEntry/Entities';
import { ict_serviceOp } from 'store/ducks/DataEntry/ICTServices';
import { globalOp } from 'store/ducks/Global';
import { getTPPColumns } from 'components/Columns/TPPTableColumns';
import SaveButton from 'components/SaveButton/SaveButton';
import useTooltipClickOutside from 'utils/hooks/useTooltipClickOutside';
import { requiredFieldsForTPP } from 'constants/requiredFields';
import CustomSelect from 'components/CustomSelect';
import * as Styled from '../../DataEntry.styled';
import { deleteRecord } from 'utils/hooks/deleteRecord';

const ThirdPartyServiceProvider: React.FC = () => {
  useTppStatus();
  const dispatch = useDispatch();
  const { query } = useFilters();
  const { t } = useTranslation();
  i18nIsoCountries.registerLocale(enLocale);
  const { selectedTPP, addRowCountTpp, tpps, certificates } = useSelector(
    (state: RootState) => state.tpp,
  );
  const { confirm } = Modal;
  const { countries } = useCountries();
  const [substitutability, setSubstitubility] = useState([]);
  const [alternatives, setAlternatives] = useState<
    { label: string; value: string }[]
  >([]);
  const [possibility, setPossibility] = useState<
    { label: string; value: string }[]
  >([]);
  const { useToken } = theme;
  const { token } = useToken();
  const [doraRegCriticality, setDoraRegCriticality] = useState<
    { label: string; value: string }[]
  >([]);
  const [validities, setValidities] = useState<
    { label: string; value: string }[]
  >([
    { label: 'Valid', value: 'valid' },
    { label: 'Not Valid', value: 'not_valid' },
    { label: 'End of Validity Close', value: 'end_of_validity_close' },
  ]);
  const [localRegCriticality, setLocalRegCriticality] = useState<
    { label: string; value: string }[]
  >([]);
  const [providerCodeTypes, setProviderCodeTypes] = useState<
    { label: string; value: string }[]
  >([]);
  const [dataSource, setDataSource] = useState<ITPP[]>([]);
  const [nonSubstReasons, setNonSubstReasons] = useState<
    { label: string; value: string }[]
  >([]);
  const [tppName, setTppName] = useState<string>('');
  const [personTypes, setPersonTypes] = useState<
    { label: string; value: string }[]
  >([]);
  const [tooltipsForData, setTooltipsForData] = useState<any[]>([]);
  const [tooltipVisibility, setTooltipVisibility] = useState<{
    [key: number]: boolean;
  }>({});
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState(10);
  const isLoggedIn = localStorage.getItem('authToken');
  const [natures, setNatures] = useState<{ label: string; value: string }[]>(
    [],
  );

  const { selectedTPP_Multi } = useSelector(
    (state: RootState) => state.ictServices,
  );

  const handlePageChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const fetchTPPbyEntity = async () => {
    try {
      const response = await tppOp.fetchTPPbyEntityName({
        entity_id: query.entityId,
        entity_name: query.entity,
      });
      const data = response?.data.third_party_service_providers;
      const mappedData = data.map((item: any, index: number) => ({
        key: index,
        ict_service_provider_identification_code:
          item.ict_service_provider_identification_code,
        id: item.id,
        notice_period: item.notice_period,
        identification_code_type: item.identification_code_type,
        tpp_code_type_id: item.tpp_code_type_id,
        non_subst_reason_id: item.non_subst_reason_id,
        third_party_service_provider_name:
          item.third_party_service_provider_name,
        person_type_id: item.person_type_id,
        headquarters_country: item.headquarters_country,
        iso_code: item.iso_code,
        entity_nature: item.entity_nature,
        ultimate_parent_identification_code:
          item.ultimate_parent_identification_code,
        ultimate_parent_identification_code_type:
          item.ultimate_parent_identification_code_type,
        last_audit_date: item.last_audit_date,
        description: item.description,
        entity_id: item.entity_id,
        end_date: item.end_date ? item.end_date : '9999-01-01',
        start_date: item.start_date,
        isNew: false,
        isEdited: false,
        certificates: [],
        add_ict_service_provider_identification_code:
          item.add_ict_service_provider_identification_code,
        add_identification_code_type: item.add_identification_code_type,
        third_party_service_provider_legal_name:
          item.third_party_service_provider_legal_name,
        dora_regulator_criticality_id: item.dora_regulator_criticality_id,
        dora_regulator_criticality_name: item.dora_regulator_criticality_name,
        local_regulator_criticality_id: item.local_regulator_criticality_id,
        local_regulator_criticality_name: item.local_regulator_criticality_name,
        alternative_providers_identified_id:
          item.alternative_providers_identified_id,
        alternative_providers_identified_name:
          item.alternative_providers_identified_name,
        exit_plan_exists_id: item.exit_plan_exists_id,
        exit_plan_exists_name: item.exit_plan_exists_name,
        possibility_of_reintegration_id: item.possibility_of_reintegration_id,
        possibility_of_reintegration_name:
          item.possibility_of_reintegration_name,
        rank: item.rank,
        recipient_identification_code: item.recipient_identification_code,
        recipient_identification_type: item.recipient_identification_type,
        substitutability_of_ict_provider_name:
          item.substitutability_of_ict_provider_name,
        substitutability_of_ict_provider_id:
          item.substitutability_of_ict_provider_id,
      }));
      setDataSource(mappedData);
      const providerNames = mappedData.map(
        (item: { third_party_service_provider_name: any }) =>
          item.third_party_service_provider_name,
      );
      dispatch(setProviderNames(providerNames));
    } catch (error) {
      console.log(error, 'error');
    }
  };

  const getCertificates = async () => {
    try {
      if (!selectedTPP_Multi || selectedTPP_Multi.length === 0) {
        return;
      }

      const selectedIds = selectedTPP_Multi.map((tpp) => tpp.value);

      const matchedTPPs = dataSource.filter((item: any) =>
        selectedIds.includes(item.id.toString()),
      );

      if (matchedTPPs.length === 0) {
        return;
      }

      const allCertificates: any[] = [];

      for (const tpp of matchedTPPs) {
        const response = await tppOp.setCertificates(
          tpp.third_party_service_provider_name,
          String(tpp.id),
        );

        const certificates = response.certificates || [];

        const mappedData = certificates.map(
          (certificate: any, index: number) => ({
            key: `${tpp.id}-${index}`,
            id: certificate.id,
            description: certificate.description,
            validity: certificate.validity,
            date_of_issuance: certificate.date_of_issuance,
            certificate_registry: certificate.certificate_registry,
            date_of_certificate_register:
              certificate.date_of_certificate_register,
            third_party_service_provider_id:
              certificate.third_party_service_provider_id,
            start_date: certificate.start_date,
            end_date: certificate.end_date || '9999-01-01',
            isNew: false,
            isEdited: false,
            tpp_name: response.tpp_name,
          }),
        );

        allCertificates.push(...mappedData);
      }

      setTooltipsForData(allCertificates);
    } catch (error) {
      console.log('Error:', error);
    }
  };

  const displayedData = useMemo(() => {
    if (!selectedTPP_Multi || selectedTPP_Multi.length === 0) {
      return dataSource.filter((item) => item.isNew);
    }
    const filteredApiData = dataSource.filter(
      (item) =>
        !item.isNew &&
        selectedTPP_Multi.some((tpp) => tpp.value === item.id.toString()),
    );
    const manualRows = dataSource.filter((item) => item.isNew);

    return [...filteredApiData, ...manualRows];
  }, [dataSource, selectedTPP_Multi]);

  const fetchReintegrationPossibilities = async () => {
    try {
      const response = await ict_serviceOp.fetchReintegrationPossibilities(
        query.entityId,
      );
      if (response?.data.possibility_of_reintegration) {
        const formattedOptions = response.data.possibility_of_reintegration.map(
          (possibility: { description: any; id: any }) => ({
            label: possibility.description,
            value: possibility.id,
          }),
        );
        setPossibility(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchProviderCodeTypes = async () => {
    try {
      const response = await tppOp.fetchProviderCodeTypes();
      if (response?.data.third_party_service_provider_code_types) {
        const formattedOptions =
          response.data.third_party_service_provider_code_types.map(
            (reasons: { name: any; id: any }) => ({
              label: reasons.name,
              value: reasons.id,
            }),
          );
        setProviderCodeTypes(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchNatures = async () => {
    try {
      const response = await entityOp.fetchNatureID();
      if (response?.data.nature_of_entities) {
        const formattedOptions = response.data.nature_of_entities.map(
          (reasons: { name: any; id: any }) => ({
            label: reasons.name,
            value: reasons.id,
          }),
        );
        setNatures(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    dispatch(set_certificates(tooltipsForData));
  }, [tooltipsForData, dispatch]);

  const fetchSubstitutability = async () => {
    try {
      const response = await globalOp.fetchSubstitutability(query.entityId);
      if (response?.data.substitutability_of_ict_provider) {
        const formattedOptions =
          response.data.substitutability_of_ict_provider.map(
            (subst: { description: any; id: any }) => ({
              label: subst.description,
              value: subst.id,
            }),
          );
        setSubstitubility(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchNonSubstReasons = async () => {
    try {
      const response = await tppOp.fetchNonSubstReasons();
      if (response?.data.non_subst_reasons) {
        const formattedOptions = response.data.non_subst_reasons.map(
          (reasons: { name: any; id: any }) => ({
            label: reasons.name,
            value: reasons.id,
          }),
        );
        setNonSubstReasons(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchPersonTypes = async () => {
    try {
      const response = await tppOp.fetchPersonTypes();
      if (response?.data) {
        const formattedOptions = response.data.map(
          (reasons: { name: any; id: any }) => ({
            label: reasons.name,
            value: reasons.id,
          }),
        );
        setPersonTypes(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (query.entity) {
      setDataSource([]);
      fetchTPPbyEntity();
      fetchNonSubstReasons();
      fetchSubstitutability();
      fetchPersonTypes();
      fetchReintegrationPossibilities();
      fetchAlternativesExit();
      fetchDoraRegulatorCriticality();
      fetchLocalRegCriticality();
      fetchNatures();
      fetchProviderCodeTypes();
    } else {
      dispatch(set_tpps([]));
      dispatch(set_addRowCountTpp(0));
    }
  }, [query.entity]);

  useEffect(() => {
    getCertificates();
  }, [selectedTPP_Multi, dataSource]);

  const handleAddRow = (record?: any, index?: number) => {
    dispatch(set_isAddRowTpp(true));
    const idTpp = generateUniqueId();
    const newData: ITPP = {
      key: idTpp,
      id: idTpp,
      ict_service_provider_identification_code: '',
      notice_period: null,
      identification_code_type: '',
      tpp_code_type_id: '',
      substitutability: null,
      non_subst_reason_id: '',
      third_party_service_provider_name: '',
      person_type_id: '',
      headquarters_country: '',
      iso_code: '',
      entity_nature: '',
      ultimate_parent_identification_code: '',
      ultimate_parent_identification_code_type: '',
      add_ict_service_provider_identification_code: '',
      add_identification_code_type: '',
      third_party_service_provider_legal_name: '',
      last_audit_date: '',
      description: '',
      entity_id: Number(query.entityId),
      dora_regulator_criticality_id: null,
      dora_regulator_criticality_name: '',
      local_regulator_criticality_id: null,
      local_regulator_criticality_name: '',
      alternative_providers_identified_id: '',
      alternative_providers_identified_name: '',
      exit_plan_exists_id: '',
      exit_plan_exists_name: '',
      possibility_of_reintegration_id: '',
      possibility_of_reintegration_name: '',
      end_date: '9999-01-01',
      rank: '',
      recipient_identification_code: '',
      recipient_identification_type: '',
      substitutability_of_ict_provider_name: '',
      substitutability_of_ict_provider_id: '',
      start_date: '',
      isNew: true,
      certificates: [],
    };

    dispatch(set_tppId(newData.id));
    setDataSource((prev) => [...prev, newData]);
  };

  const fetchAlternativesExit = async () => {
    try {
      const response = await ict_serviceOp.fetchYesNo();
      if (response?.data.yes_no_statuses) {
        const formattedOptions = response.data.yes_no_statuses.map(
          (reasons: { description: any; id: any }) => ({
            label: reasons.description,
            value: reasons.id,
          }),
        );
        setAlternatives(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchDoraRegulatorCriticality = async () => {
    try {
      const response = await tppOp.fetchDoraRegulatorCriticality();
      if (response?.data.dora_regulator_criticality) {
        const formattedOptions = response.data.dora_regulator_criticality.map(
          (reasons: { name: any; id: any }) => ({
            label: reasons.name,
            value: reasons.id,
          }),
        );
        setDoraRegCriticality(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchLocalRegCriticality = async () => {
    try {
      const response = await ict_serviceOp.fetchLocalRegulatorCriticality();
      if (response?.data.local_regulator_criticality) {
        const formattedOptions = response.data.local_regulator_criticality.map(
          (reasons: { name: any; id: any }) => ({
            label: reasons.name,
            value: reasons.id,
          }),
        );
        setLocalRegCriticality(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleSave = (row: ITPP) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    if (index > -1) {
      newData.splice(index, 1, {
        ...newData[index],
        ...row,
        isEdited: !newData[index]?.isNew || false,
      });
      setDataSource(newData);
    } else {
      newData.push({
        ...row,
        isEdited: false,
      });
    }
    if (row.isNew) {
      dispatch(set_nameOfTpp(row.third_party_service_provider_name));
    }
    setDataSource(newData);
    const updatedTpps = [...tpps];
    const tppIndex = updatedTpps.findIndex((item) => item.key === row.key);

    if (tppIndex > -1) {
      updatedTpps[tppIndex] = { ...updatedTpps[tppIndex], ...row };
    } else {
      updatedTpps.push(row);
    }
  };

  const areAllFieldsFilled = (tpps: ITPP[]) => {
    const newlyAddedTpps = tpps.filter((tpp) => tpp.isNew);
    return newlyAddedTpps.every((item) => {
      const invalidKeys = Object.entries(item).filter(([key, value]) => {
        if (key === 'description') return false;
        return value === '' || value === null || value === undefined;
      });
      return invalidKeys.length === 0;
    });
  };

  const handleOpenTooltip = (certificateId: string) => {
    setTooltipVisibility((prev) => ({ ...prev, [certificateId]: true }));
  };

  const handleCloseTooltip = (certificateId: string) => {
    setTooltipVisibility((prev) => ({ ...prev, [certificateId]: false }));
  };
  useTooltipClickOutside({
    tooltipVisibility: tooltipVisibility,
    setTooltipVisibility: setTooltipVisibility,
  });

  const handleRemoveRow = (id: number | string) => {
    Modal.confirm({
      title: 'Are you sure?',
      onOk: () => {
        setDataSource((prev) => prev.filter((item) => item.id !== id));

        notification.success({
          message: 'Info',
          description: 'The row has been successfully removed.',
        });
      },
    });
  };
  const handleInactivate = (record: ITPP) => {
    if (!record.id) {
      notification.error({
        message: 'Info',
        description: 'Id is missing.',
      });
      return;
    }
    deleteRecord({
      id: Number(record.id),
      tableName: 'third_party_service_provider',
      onSuccess: async () => {
        await fetchTPPbyEntity();
        setDataSource([]);
      },
      onBlocked: async (data) => {
        const onlyCertificatesOrCosts = data.references?.every(
          (ref: any) => ref.table === 'certificates' || ref.table === 'costs',
        );

        if (onlyCertificatesOrCosts) {
          await tppOp.inactivateTPP([Number(record.id)]);
          await fetchTPPbyEntity();
          setDataSource([]);
          return true;
        }
        return false;
      },
    });
  };

  const handleDeleteCertificate = (certificateId: number) => {
    confirm({
      title: 'Are you sure you want to delete this certificate?',
      content: 'This action cannot be undone.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        const certificateToDelete = tooltipsForData.find(
          (certificate) => certificate.id === certificateId,
        );

        if (certificateToDelete?.isNew) {
          setTooltipsForData((prevData) =>
            prevData.filter((certificate) => certificate.id !== certificateId),
          );
        } else {
          try {
            deleteRecord({
              id: certificateId,
              tableName: 'certificates',
              onSuccess: async () => {
                await fetchTPPbyEntity();
                await getCertificates();
              },
            });
            // setTooltipsForData((prevData) =>
            //   prevData.filter((activity) => activity.id !== activityId)
            // );
            // dispatch(set_activitiesByTypeEntity(tooltipsForData));
          } catch (error) {
            console.log('Failed to inactivate activity:', error);
          }
        }
      },
      onCancel: () => {},
    });
  };
  useEffect(() => {
    if (addRowCountTpp === 1) {
      const allFieldsFilled = areAllFieldsFilled(tpps);
      if (allFieldsFilled) {
        dispatch(set_isTppAdded(true));
      } else {
        dispatch(set_isTppAdded(false));
      }
    }
  }, [tpps, addRowCountTpp]);

  const handleSubmit = async () => {
    const changedData = dataSource.filter(
      (item) => item.isNew || item.isEdited,
    );

    changedData.forEach((item) => {
      if (item.notice_period) {
        item.notice_period = Number(item.notice_period);
      }
    });

    const changedCertificates = certificates.filter(
      (item) => item.isNew || item.isEdited,
    );
    if (changedData.length === 0 && changedCertificates.length === 0) {
      notification.warning({
        message: 'Info',
        description: 'No new data has been changed',
        placement: 'topRight',
        duration: 20,
      });
      return;
    }

    const hasEmptyFields = changedData.some((item) =>
      (requiredFieldsForTPP as (keyof typeof item)[]).some((field) => {
        return item[field] === '';
      }),
    );

    if (hasEmptyFields) {
      notification.error({
        message: '',
        description:
          'Please complete all the fields highlighted in yellow and make sure to enter a start date before submitting.',
        placement: 'topRight',
        duration: 20,
      });
      return;
    }

    const newlyAddedTpps = tpps.filter((tpp) => tpp.isNew);
    if (newlyAddedTpps.length !== 0) {
      const allFieldsFilled = areAllFieldsFilled(newlyAddedTpps);
      if (!allFieldsFilled) {
      } else {
        dispatch(set_isTppAdded(true));
      }
    }
    try {
      const response = await tppOp.fetchTPP({
        entity_name: query.entity,
        ThirdPartyServiceProvider: changedData,
        token: isLoggedIn,
      });
      dispatch(set_isTppUpdated(true));
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const responseCertificates = await tppOp.insertCertificates({
        tpp_name: changedCertificates[0]?.tpp_name || '',
        entity_name: query.entity,
        data_list: changedCertificates,
        token: isLoggedIn,
      });
      await fetchTPPbyEntity();

      // Reset isNew and isEdited flags after successful save
      setDataSource((prev) =>
        prev.map((item) => ({
          ...item,
          isNew: false,
          isEdited: false,
        })),
      );
      setTooltipsForData((prev) =>
        prev.map((cert) => ({
          ...cert,
          isNew: false,
          isEdited: false,
        })),
      );
    } catch (error) {
      notification.error({
        message: 'Info',
        description: 'There was an error sending the data. Please try again.',
        placement: 'topRight',
        duration: 20,
      });
    }
  };

  const columns = getTPPColumns(
    handleSave,
    countries,
    providerCodeTypes,
    substitutability,
    doraRegCriticality,
    alternatives,
    natures,
    possibility,
    localRegCriticality,
    nonSubstReasons,
    personTypes,
    t,
  );
  const defaultColumns: EditableColumnType[] = [
    {
      title: <>Certificates</>,
      dataIndex: 'certificates',
      render: (_: any, record: any) => {
        const relatedCertificates = tooltipsForData.filter(
          (cert) => cert.third_party_service_provider_id === record.id,
        );
        const handleAddCertificate = (recordId: any) => {
          const newCertificate = {
            id: generateUniqueId(),
            description: '',
            validity: '',
            date_of_issuance: '',
            certificate_registry: '',
            date_of_certificate_register: '',
            third_party_service_provider_id: recordId,
            tpp_name: record.third_party_service_provider_name,
            start_date: '',
            end_date: '9999-01-01',
            isNew: true,
          };
          setTooltipsForData((prevData) => [...prevData, newCertificate]);
          dispatch(set_certificates(tooltipsForData));
        };

        const handleUpdateCertificate = (
          certificateId: string,
          field: string,
          value: any,
        ) => {
          setTooltipsForData((prevData) =>
            prevData.map((cert) =>
              cert.id === certificateId
                ? { ...cert, [field]: value, isEdited: true }
                : cert,
            ),
          );
          dispatch(set_certificates(tooltipsForData));
        };

        return (
          <div>
            {relatedCertificates.map((certificate) => (
              <Styled.TooltipContainer
                key={certificate.id}
                title={
                  <div>
                    <Styled.TooltipButtonContainer>
                      <Button
                        size="small"
                        type="text"
                        danger
                        onClick={() => handleDeleteCertificate(certificate.id)}
                      >
                        <DeleteOutlined />
                      </Button>
                      <Button
                        size="small"
                        type="text"
                        onClick={() => handleCloseTooltip(certificate.id)}
                      >
                        Add / Amend
                      </Button>
                    </Styled.TooltipButtonContainer>
                    <Styled.InputField>
                      <strong>Description</strong>
                      <Input
                        defaultValue={certificate.description || ''}
                        onChange={(e) =>
                          handleUpdateCertificate(
                            certificate.id,
                            'description',
                            e.target.value,
                          )
                        }
                      />
                    </Styled.InputField>
                    <Styled.InputField>
                      <strong>
                        Validity <Styled.Asterisx>*</Styled.Asterisx>
                      </strong>
                      <CustomSelect
                        options={validities}
                        value={certificate.validity}
                        onChange={(value: string) => {
                          const selectedValidity = validities.find(
                            (v) => v.value === value,
                          );
                          const labelToSend = selectedValidity
                            ? selectedValidity.label
                            : value;

                          handleUpdateCertificate(
                            certificate.id,
                            'validity',
                            labelToSend,
                          );
                        }}
                        filterOption={(
                          input: string,
                          option: { label: string },
                        ) =>
                          option?.label
                            ?.toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      />
                    </Styled.InputField>
                    <Styled.InputField>
                      <strong>Date of Issuance</strong>
                      <DatePicker
                        format="YYYY-MM-DD"
                        value={
                          certificate.date_of_issuance
                            ? dayjs(certificate.date_of_issuance, 'YYYY-MM-DD')
                            : null
                        }
                        onChange={(date) => {
                          const formattedDate =
                            date?.format('YYYY-MM-DD') || '';
                          handleUpdateCertificate(
                            certificate.id,
                            'date_of_issuance',
                            formattedDate,
                          );
                        }}
                      />
                    </Styled.InputField>
                    <Styled.InputField>
                      <strong>
                        Certificate Registry
                        <Styled.Asterisx>*</Styled.Asterisx>
                      </strong>
                      <Input
                        defaultValue={certificate.certificate_registry}
                        onChange={(e) =>
                          handleUpdateCertificate(
                            certificate.id,
                            'certificate_registry',
                            e.target.value,
                          )
                        }
                      />
                    </Styled.InputField>

                    <Styled.InputField>
                      <strong>
                        Date of Certificate Register
                        <Styled.Asterisx>*</Styled.Asterisx>
                      </strong>
                      <DatePicker
                        format="YYYY-MM-DD"
                        value={
                          certificate.date_of_certificate_register
                            ? dayjs(
                                certificate.date_of_certificate_register,
                                'YYYY-MM-DD',
                              )
                            : null
                        }
                        onChange={(date) => {
                          const formattedDate =
                            date?.format('YYYY-MM-DD') || '';
                          handleUpdateCertificate(
                            certificate.id,
                            'date_of_certificate_register',
                            formattedDate,
                          );
                        }}
                      />
                    </Styled.InputField>
                    <Styled.InputField>
                      <strong>
                        Certificate Valid from{' '}
                        <Styled.Asterisx>*</Styled.Asterisx>
                      </strong>
                      <DatePicker
                        format="YYYY-MM-DD"
                        value={
                          certificate.start_date
                            ? dayjs(certificate.start_date, 'YYYY-MM-DD')
                            : null
                        }
                        onChange={(date) => {
                          const formattedDate =
                            date?.format('YYYY-MM-DD') || '';
                          handleUpdateCertificate(
                            certificate.id,
                            'start_date',
                            formattedDate,
                          );
                        }}
                      />
                    </Styled.InputField>

                    <Styled.InputField>
                      <strong>
                        Certificate Valid to
                        <Styled.Asterisx>*</Styled.Asterisx>
                      </strong>
                      <DatePicker
                        format="YYYY-MM-DD"
                        value={
                          certificate.end_date
                            ? dayjs(certificate.end_date, 'YYYY-MM-DD')
                            : dayjs('9999-01-01')
                        }
                        onChange={(date) => {
                          const formattedDate =
                            date?.format('YYYY-MM-DD') || dayjs('9999-01-01');
                          handleUpdateCertificate(
                            certificate.id,
                            'end_date',
                            formattedDate,
                          );
                        }}
                      />
                    </Styled.InputField>

                    <Styled.InputField>
                      <strong>ICT third-party service provider name</strong>
                      <Input
                        value={
                          record.third_party_service_provider_name || tppName
                        }
                        disabled
                      />
                    </Styled.InputField>
                  </div>
                }
                visible={tooltipVisibility[certificate.id] || false}
                trigger="click"
              >
                <Styled.OpenTooltipBtn
                  type="link"
                  onClick={() => handleOpenTooltip(certificate.id)}
                >
                  <Input
                    value={certificate.description || 'Add Certificate'}
                    readOnly
                    addonAfter={<FolderOpenOutlined />}
                  />
                </Styled.OpenTooltipBtn>
              </Styled.TooltipContainer>
            ))}

            <Button
              type="dashed"
              onClick={() => {
                handleAddCertificate(record.id);
              }}
              icon={<PlusOutlined />}
            ></Button>
            <Styled.GlobalStyle />
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record) => (
        <>
          {record.isNew ? (
            <Button danger onClick={() => handleRemoveRow(record.id)}>
              Remove
            </Button>
          ) : (
            <Button danger onClick={() => handleInactivate(record as ITPP)}>
              Soft delete
            </Button>
          )}
        </>
      ),
    },
  ];
  const allColumns = [...columns, ...defaultColumns];
  return (
    dataSource && (
      <div>
        <Styled.HighlightedTable>
          <CustomTable
            columns={allColumns}
            dataSource={displayedData}
            handleSave={handleSave}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              onChange: handlePageChange,
              showSizeChanger: true,
            }}
          />
        </Styled.HighlightedTable>
        <Styled.ButtonRow>
          {!dataSource.length && (
            <Styled.AreaButton
              token={token}
              type="primary"
              onClick={() => handleAddRow()}
            >
              {t('GENERAL.ADD_FIRST_ROW')}
            </Styled.AreaButton>
          )}
          {dataSource.length > 0 && (
            <Styled.AreaButton
              token={token}
              type="primary"
              onClick={() => handleAddRow()}
            >
              Add new row
            </Styled.AreaButton>
          )}
          {dataSource.length > 0 && (
            <SaveButton
              token={token}
              hasChanges={
                dataSource.some((i) => i.isNew || i.isEdited) ||
                tooltipsForData.some((c) => c.isNew || c.isEdited)
              }
              onClick={handleSubmit}
            />
          )}
        </Styled.ButtonRow>
      </div>
    )
  );
};

export default ThirdPartyServiceProvider;
